<template>
  <div>
    <div class="d-flex justify-space-between align-start flex-wrap" style="gap: 12px">
      <div class="text-caption grey--text" style="max-width: 900px">
        Selecione os dias da semana e defina quais componentes curriculares serão ministrados em
        cada horário. Cada horário aceita no máximo 1 componente não complementar, mais quantos
        componentes complementares forem necessários.
      </div>
      <div class="d-flex flex-wrap" style="gap: 4px">
        <v-btn
          v-for="dia in grade.dias"
          :key="dia.id"
          :id="`grade.dia-${dia.id}`"
          x-small
          rounded
          :color="diasAtivos.has(dia.id) ? 'orange' : 'grey lighten-2'"
          :dark="diasAtivos.has(dia.id)"
          @click="alternarDia(dia.id)"
        >
          {{ dia.descricao.slice(0, 3).toUpperCase() }}
        </v-btn>
      </div>
    </div>

    <v-alert v-if="gradeLegadaPendente" dense text type="warning" class="mt-3">
      Esta gestão possui dias e horários configurados antes desta atualização, sem componente
      curricular definido. Reconfigure a grade abaixo.
    </v-alert>

    <div class="d-flex align-center grey--text text-caption mt-3 mb-2">
      <v-icon small class="mr-2">mdi-lock</v-icon>
      Horário com não complementar de outra gestão só aceita complementar aqui.
    </div>

    <div style="position: relative">
      <div
        v-if="carregando"
        class="d-flex justify-center align-center"
        style="
          position: absolute;
          inset: 0;
          background: rgba(255, 255, 255, 0.6);
          z-index: 1;
          pointer-events: none;
        "
      >
        <v-progress-circular indeterminate color="primary" size="24" />
      </div>

      <div v-if="diasAtivosOrdenados.length === 0" class="text-center pa-6 grey--text">
        Selecione ao menos um dia da semana acima para montar a grade de horários.
      </div>

      <div v-else style="overflow-x: auto">
        <table
          class="grade-dias-horarios"
          style="width: 100%; border-collapse: separate; border-spacing: 4px 6px"
        >
          <thead>
            <tr>
              <th style="text-align: left; padding: 6px 10px; min-width: 130px">HORÁRIO</th>
              <th
                v-for="dia in diasAtivosOrdenados"
                :key="dia.id"
                class="orange white--text text-uppercase"
                style="padding: 8px 10px; border-radius: 6px; font-size: 0.8rem"
              >
                {{ dia.descricao }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="horario in grade.horarios" :key="horario.id">
              <td
                class="text-caption grey--text"
                style="
                  padding: 4px 10px;
                  white-space: nowrap;
                  border: 1px solid rgba(0, 0, 0, 0.12);
                  border-radius: 4px;
                "
              >
                {{ horario.descricao }}
              </td>
              <td
                v-for="dia in diasAtivosOrdenados"
                :key="dia.id"
                class="rounded pa-1"
                style="
                  min-width: 190px;
                  min-height: 40px;
                  vertical-align: top;
                  box-sizing: border-box;
                  border: 1px solid rgba(0, 0, 0, 0.12);
                "
              >
                <div
                  v-if="celulaBloqueada(dia.id, horario.id)"
                  class="d-flex align-center grey--text text-caption mb-1"
                >
                  <v-icon small class="mr-1">mdi-lock</v-icon>
                  Só complementar aqui
                </div>
                <div
                  v-if="celulaPendente(dia.id, horario.id)"
                  class="d-flex align-center grey--text text-caption mb-1"
                >
                  <v-icon small color="orange" class="mr-1">mdi-information-outline</v-icon>
                  Reconfigure este horário
                </div>

                <div
                  v-if="
                    !idsDaCelula(dia.id, horario.id).length && !celulaPendente(dia.id, horario.id)
                  "
                  class="text-caption grey--text"
                >
                  Horário disponível
                </div>

                <div class="d-flex align-center justify-space-between" style="gap: 4px">
                  <div class="d-flex flex-wrap align-center" style="gap: 4px">
                    <v-chip
                      v-for="componenteId in idsDaCelula(dia.id, horario.id)"
                      :key="componenteId"
                      :id="`grade.componente-${dia.id}-${horario.id}-${componenteId}`"
                      x-small
                      close
                      dark
                      :color="corDoComponente(componenteId)"
                      @click:close="removerDaCelula(dia.id, horario.id, componenteId)"
                    >
                      {{ descricaoDoComponente(componenteId) }}
                    </v-chip>
                  </div>

                  <!--
                      :key inclui os ids já presentes na célula (não só dia+horário): o
                      ativador desse v-menu é um slot escopado e, ao adicionar o 1º componente
                      numa célula até então vazia, o Vue as vezes faz só um patch incremental
                      em vez de recriar o nó — nesse patch o botão "+" podia ficar posicionado
                      ANTES do chip recém-criado quando os dois dividiam o mesmo container
                      flex-wrap (inverte visualmente esquerda/direita). Trocar a key força esse
                      v-menu a ser desmontado e remontado do zero a cada adição/remoção na
                      célula — mantido por segurança mesmo agora que o botão é um irmão fixo à
                      direita, fora do container dos chips.
                    -->
                  <v-menu
                    v-if="componentesDisponiveis(dia.id, horario.id).length"
                    :key="`adicionar-${dia.id}-${horario.id}-${idsDaCelula(dia.id, horario.id).join(
                      '-'
                    )}`"
                    offset-y
                    max-height="260"
                  >
                    <template #activator="{ on, attrs }">
                      <v-btn
                        :id="`grade.adicionar-${dia.id}-${horario.id}`"
                        icon
                        x-small
                        style="flex: none"
                        v-bind="attrs"
                        v-on="on"
                      >
                        <v-icon small>mdi-plus-circle-outline</v-icon>
                      </v-btn>
                    </template>
                    <v-list dense>
                      <v-list-item
                        v-for="componente in componentesDisponiveis(dia.id, horario.id)"
                        :key="componente.id"
                        @click="adicionarNaCelula(dia.id, horario.id, componente.id)"
                      >
                        <v-list-item-title>
                          {{ componente.descricao }}
                          <span v-if="componente.extra_curricular" class="grey--text"
                            >(complementar)</span
                          >
                        </v-list-item-title>
                      </v-list-item>
                    </v-list>
                  </v-menu>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <v-alert
      v-if="!carregando"
      dense
      text
      :type="grade.resumo.total_horarios > 0 ? 'success' : 'info'"
      class="mt-3 mb-0"
    >
      <template v-if="grade.resumo.total_horarios > 0">
        {{ grade.resumo.total_horarios }} horário(s) selecionado(s) em
        {{ grade.resumo.total_dias }} dia(s) da semana.
        <div class="text-caption">{{ grade.resumo.dias_descricoes.join(", ") }}</div>
      </template>
      <template v-else> Nenhum dia e horário selecionado ainda. </template>
    </v-alert>

    <div class="mt-3 d-flex flex-wrap text-caption grey--text" style="gap: 12px">
      <span
        ><v-icon x-small color="orange">mdi-plus-circle-outline</v-icon> Adicionar componente</span
      >
      <span
        ><v-icon x-small>mdi-lock</v-icon> Não complementar de outra gestão (só complementar
        aqui)</span
      >
      <span
        ><v-icon x-small color="orange">mdi-information-outline</v-icon> Componente sem descrição
        (dado legado)</span
      >
    </div>
  </div>
</template>

<script>
export default {
  name: "EGradeDiasHorarios",
  props: {
    value: {
      type: Array,
      default: () => [],
    },
    grade: {
      type: Object,
      required: true,
    },
    carregando: {
      type: Boolean,
      default: false,
    },
    // Snapshot fixo de "esta gestão já tinha dias/horários sem componente definido, salvos
    // antes desta feature existir" — calculado uma vez ao carregar a tela, não a cada preview
    // (senão uma célula que o próprio usuário acabou de marcar, ainda sem escolher o
    // componente, seria confundida com esse aviso de dado legado).
    gradeLegadaPendente: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      diasAtivosManual: [],
    };
  },
  computed: {
    diasComCelulas() {
      return new Set(this.value.map((c) => c.dia_id));
    },
    diasAtivos() {
      return new Set([...this.diasAtivosManual, ...this.diasComCelulas]);
    },
    diasAtivosOrdenados() {
      return (this.grade.dias || []).filter((dia) => this.diasAtivos.has(dia.id));
    },
  },
  methods: {
    // Um mesmo dia+horário agora aceita várias linhas em value (uma por componente) — no
    // máximo 1 não complementar, mais quantos complementares forem necessários.
    itensDaCelula(diaId, horarioId) {
      return this.value.filter((c) => c.dia_id === diaId && c.horario_id === horarioId);
    },
    idsDaCelula(diaId, horarioId) {
      return this.itensDaCelula(diaId, horarioId)
        .map((c) => c.disciplina_id)
        .filter((id) => id !== null && id !== undefined);
    },
    celulaSelecionada(diaId, horarioId) {
      return this.itensDaCelula(diaId, horarioId).length > 0;
    },
    // Só reflete dado legado real (linha salva sem disciplina_id, de antes desta feature) —
    // o fluxo de adicionar hoje sempre escolhe o componente no mesmo passo, nunca deixa uma
    // linha "pendurada" sem disciplina.
    celulaPendente(diaId, horarioId) {
      return this.itensDaCelula(diaId, horarioId).some(
        (c) => c.disciplina_id === null || c.disciplina_id === undefined
      );
    },
    temNaoComplementar(diaId, horarioId) {
      return this.idsDaCelula(diaId, horarioId).some((id) => {
        const componente = this.componenteDoId(id);
        return componente && !componente.extra_curricular;
      });
    },
    componenteDoId(disciplinaId) {
      return (this.grade.componentes || []).find((c) => c.id === disciplinaId);
    },
    descricaoDoComponente(disciplinaId) {
      const componente = this.componenteDoId(disciplinaId);
      return componente ? componente.descricao : "Componente removido";
    },
    corDoComponente(disciplinaId) {
      const componente = this.componenteDoId(disciplinaId);
      return componente ? componente.cor : "grey";
    },
    // Componentes que ainda podem ser adicionados nesta célula: exclui os já adicionados e,
    // se já houver um não complementar (nesta gestão OU numa célula bloqueada por outra
    // gestão), exclui também os demais não complementares — só complementar pode se somar.
    componentesDisponiveis(diaId, horarioId) {
      const idsJaNaCelula = new Set(this.idsDaCelula(diaId, horarioId));
      const jaTemNaoComplementar =
        this.temNaoComplementar(diaId, horarioId) || this.celulaBloqueada(diaId, horarioId);

      return (this.grade.componentes || []).filter((componente) => {
        if (idsJaNaCelula.has(componente.id)) return false;
        if (jaTemNaoComplementar && !componente.extra_curricular) return false;
        return true;
      });
    },
    celulaBloqueada(diaId, horarioId) {
      const celula = (this.grade.celulas || []).find(
        (c) => c.dia_id === diaId && c.horario_id === horarioId
      );
      return !!(celula && celula.bloqueada);
    },
    estiloCelula(diaId, horarioId) {
      if (!this.celulaSelecionada(diaId, horarioId)) return {};
      return { backgroundColor: "rgba(0, 0, 0, 0.03)" };
    },
    alternarDia(diaId) {
      if (this.diasAtivos.has(diaId)) {
        this.diasAtivosManual = this.diasAtivosManual.filter((id) => id !== diaId);
        const novoValue = this.value.filter((c) => c.dia_id !== diaId);
        this.$emit("input", novoValue);
      } else {
        this.diasAtivosManual = [...this.diasAtivosManual, diaId];
      }
    },
    adicionarNaCelula(diaId, horarioId, disciplinaId) {
      // Uma célula "pendente" (dado legado, de antes desta feature: dia+horário salvos sem
      // disciplina) fica com uma linha { disciplina_id: null } no value — ao adicionar um
      // componente de verdade nessa célula, essa linha pendente precisa sair, senão ela
      // continua contando como "componente faltando" e o "Aplicar" nunca deixa de bloquear,
      // mesmo já tendo escolhido o componente.
      const novoValue = [
        ...this.value.filter(
          (c) => !(c.dia_id === diaId && c.horario_id === horarioId && !c.disciplina_id)
        ),
        { dia_id: diaId, horario_id: horarioId, disciplina_id: disciplinaId },
      ];
      this.$emit("input", novoValue);
    },
    removerDaCelula(diaId, horarioId, disciplinaId) {
      const novoValue = this.value.filter(
        (c) =>
          !(c.dia_id === diaId && c.horario_id === horarioId && c.disciplina_id === disciplinaId)
      );
      this.$emit("input", novoValue);
    },
  },
};
</script>

<style scoped>
/*
 * O CSS oficial do Vuetify 2.7 não define pointer-events: none no ripple do checkbox
 * (.v-input--selection-controls__ripple). Esse elemento decorativo fica posicionado sobre
 * uma área maior que o checkbox visível e, sem essa regra, intercepta cliques reais de
 * mouse — dando a sensação de precisar clicar duas vezes para marcar/desmarcar.
 */
::v-deep .v-input--selection-controls__ripple {
  pointer-events: none;
}
</style>
